import {ComponentLink, Link, makeChain} from "@Common/react/cst2";
import {Load2}                          from "@Common/react/load2";
import {UseState}                       from "@Common/react/use-state";
import {Types}                          from "@Common/types";
import {upperCase1}                     from "@Common/utils/strings";

import {consumeContext}                 from "./consume-context";

type GetSchemeResponse          = Types.Apis.Payloads.Next.Scheme.GetScheme.Response;
type PostXpressoSchemeRequest   = Types.Apis.Payloads.Next.Scheme.PostXpressoScheme.Request;
type SchemeXpressoPatch         = Parameters<Types.Xpresso.Loaders.ContextNameMap['themeScheme']['xpresso']['patch']>[0];

export const loadScheme = ({next: rootNext, companyXdocsUrl=null}:{
    companyXdocsUrl?: string,
    next            : Link<JSX.Element, Types.Xpresso.Loaders.ContextNameMap['themeScheme']>,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then("schemeLoad", ({apis}, next:ComponentLink<typeof Load2<GetSchemeResponse>>) => Load2({
        fetch: () => apis.next.getThemeScheme(companyXdocsUrl),
        next,
    }))
    .then("xpressoPatchState", ({}, next:ComponentLink<typeof UseState<{
        active  : boolean,
        patch   : SchemeXpressoPatch,
    }>>) => UseState({
        next,
        initValue: {active: true, patch: {}},
    }))
    .then(({apis, schemeLoad, xpressoPatchState}) => {
        if (schemeLoad.loading) {
            return rootNext(undefined);
        }
        const reload = async () => {
            const loaded = await schemeLoad.reload();
            xpressoPatchState.change((prev) => ({...prev, patch: {}}));
            return loaded;
        };
        return rootNext({
            ...schemeLoad.value,
            reload,
            xpresso: {
                ...schemeLoad.value.xpresso,
                ...(
                    xpressoPatchState.value.active
                    ? xpressoPatchAsPartialScheme(xpressoPatchState.value.patch)
                    : {}
                ),
                patch           : (patch) => xpressoPatchState.change(
                    (prev) => ({
                        active: prev.active,
                        patch: !patch ? {} : Object.entries(patch).reduce((final: SchemeXpressoPatch, [key, value]) =>
                        {
                            if (value === undefined) {
                                delete final[key];
                            } else {
                                final[key] = value;
                            }
                            return final;
                        }, prev.patch)
                    }),
                ),
                previewPatch    : (active:boolean) => xpressoPatchState.change((prev) => ({...prev, active})),
                previewingPatch : xpressoPatchState.value.active,
                save: async() =>
                {
                    await apis.next.saveXpressoThemeScheme(xpressoPatchAsApi(xpressoPatchState.value.patch));
                    return reload();
                }
            },
        });
    })
    .run()
);

const xpressoPatchAsApi = (xpressoPatch:SchemeXpressoPatch): PostXpressoSchemeRequest => (
    Object.entries(xpressoPatch).reduce((final: PostXpressoSchemeRequest, [key, value]) =>
    {
        if ((key === "headerLogo" || key === "headerBanner") && !(value instanceof File)) {
            if (typeof value === "string") {
                final[`${key}Url`] = value;
            } else if (value === null) {
                final[`delete${upperCase1(key)}`] = true;
            }
        } else {
            final[key] = value;
        }
        return final;
    }, {})
);

const xpressoPatchAsPartialScheme = (xpressoPatch:SchemeXpressoPatch):Partial<GetSchemeResponse['xpresso']> => (
    Object.entries(xpressoPatch).reduce((final: Partial<GetSchemeResponse['xpresso']>, [key, value]) =>
    {
        if (value instanceof File) {
            final[key] = URL.createObjectURL(value);
        } else {
            final[key] = value;
        }
        return final;
    }, {})
);

